
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo } from 'react';
import { Button, Form, Modal } from 'semantic-ui-react';
import { DatePicker } from '../../components/shared/DatePicker';
import { useTextResources } from '../../language';
import { isMobileOnly } from 'mobile-device-detect';
import PropTypes from 'prop-types';
import WeekDaysSelect from '../../components/shared/WeekDaysSelect';
import HotelsDropDown from '../../components/shared/HotelsDropDown';
import RoomTypesDropDown from '../../components/shared/RoomTypesDropDown';
import NumericInput from '../../components/shared/NumericInput';
import moment from 'moment';
import { Availability } from '../../api/services/next';
import { onError, onSuccess, onWarning } from '../../components/shared/messages';
import { PermissionsActions, UserHasPermission } from '../../components/shared/permissionsActions';
import { toServerDateFormat } from '../../components/shared/utils';
import { useLoader } from '../../hooks/useLoader';

export const EditMaxOccupancyPopup = ({ edit, setEdit, onSave }) => {
    const text = useTextResources();
    const [hotelId, setHotelId] = useState(null);
    const [dates, setDates] = useState([moment().toDate(), moment().toDate()]);
    const [days, setDays] = useState(null);
    const [roomTypes, setRoomTypes] = useState([]);
    const [maxOccupancy, setMaxOccupancy] = useState(0);
    const loader = useLoader();

    const roomTypesSpec = useMemo(() => ({ HotelId: hotelId }), [hotelId]);

    const handleHotelChange = (value) => {
        setHotelId(value);
        setRoomTypes([]); // Reset room types when hotel changes
    };

    useEffect(() => {
        if (edit && !UserHasPermission(PermissionsActions.EditMaxPax)) {
            onWarning(text('NoPermission'));
            setEdit(false);
        }
    }, [edit]);

    const resetForm = () => {
        setHotelId(null);
        setDates([moment().toDate(), moment().toDate()]);
        setDays(null);
        setRoomTypes([]);
        setMaxOccupancy(0);
    };

    const handleClose = () => {
        resetForm();
        setEdit(false);
    };

    const updateDateRange = (newFrom, newTo) => {
        if (newFrom && newTo && moment(newTo).isBefore(moment(newFrom), 'day')) {
            setDates([newFrom, newFrom]);
        } else {
            setDates([newFrom, newTo]);
        }
    };

    const setStartDate = (date) => updateDateRange(date, dates[1]);
    const setEndDate = (date) => updateDateRange(dates[0], date);

    const validateForm = () => {
        if (!hotelId) {
            onWarning(text('MissingFields').replace('{0}', text('Hotel')));
            return false;
        }
        if (!roomTypes || roomTypes.length === 0) {
            onWarning(text('MissingFields').replace('{0}', text('RoomType')));
            return false;
        }
        return true;
    };

    const saveAction = async (close) => {
        if (!validateForm()) {
            return;
        }
        try {
            loader(true);
            await Availability.UpdateMaxOccupancy({
                HotelId: hotelId,
                StartDate: toServerDateFormat(dates[0]),
                EndDate: toServerDateFormat(dates[1]),
                Weekdays: days.reduce((acc, d) => acc | (1 << d), 0),
                RoomTypeIds: roomTypes,
                MaxOccupancy: maxOccupancy
            });
            if (close) {
                resetForm();
                setEdit(false);
            }
            onSave?.();
            onSuccess();
        } catch (error) {
            onError(error);
        }
        finally {
            loader(false);
        }
    }

    const weekdates = React.useMemo(() => {
        return [{ startDate: dates[0], endDate: dates[1] }]
    }, [dates])


    return <Modal size="small" open={edit} onClose={handleClose} >
        <Modal.Header>{text('Edit')}</Modal.Header>
        <Modal.Content style={
            isMobileOnly
                ? { overflowY: 'auto' }
                : { maxHeight: 600, minHeight: 500, overflowY: 'auto' }
        }>
            <Form>
                <Form.Group>
                    <Form.Field>
                        <label>{text('FromDate')}</label>
                        <DatePicker type='basic' onChange={setStartDate} value={dates[0]} clearable={false} />
                    </Form.Field>
                    <Form.Field>
                        <label>{text('ToDate')}</label>
                        <DatePicker type='basic' onChange={setEndDate} value={dates[1]} clearable={false} />
                    </Form.Field>
                </Form.Group>
                <Form.Field
                    label={text('Weekdays')}
                    control={WeekDaysSelect}
                    onChange={setDays}
                    dates={weekdates}
                />
                <Form.Field
                     width={7}
                    required
                    label={text('Hotel')}
                    placeholder={text("Hotel")}
                    control={HotelsDropDown}
                    onChange={handleHotelChange}
                    value={hotelId}
                />
                <Form.Group>
                    <Form.Field
                        width={7}
                        label={text('RoomType')}
                        clearable
                        multiple
                        placeholder={text("RoomType")}
                        control={RoomTypesDropDown}
                        onChange={setRoomTypes}
                        value={roomTypes}
                        spec={roomTypesSpec}
                        disabled={!hotelId}
                    />
                    <Form.Field
                        label={text('Maximum') + " " + text('StayOver')}
                        control={NumericInput}
                        value={maxOccupancy}
                        onChange={(value) => setMaxOccupancy(parseInt(value))}
                        min={0}
                        digits={0}
                    />
                </Form.Group>
            </Form>
        </Modal.Content>
        <Modal.Actions>
            <Button negative onClick={handleClose}>{text('Close')}</Button>
            <Button positive onClick={() => saveAction(true)}>{text('Saveandclose')}</Button>
            <Button positive onClick={() => saveAction(false)}>{text('Save')}</Button>
        </Modal.Actions>
    </Modal>
}



EditMaxOccupancyPopup.propTypes = {
    edit: PropTypes.bool,
    setEdit: PropTypes.func,
    onSave: PropTypes.func
}
